import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  AlertTriangle,
  Check,
  Gift,
  Pencil,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { getOrderMutationErrorMessage } from "@/features/order/errors";
import {
  useRemoveOrderItem,
  useSetOrderItemBonus,
  useUpdateOrderItem,
} from "@/features/order/hooks";
import type {
  EnrichedOrderItem,
  OrderStatus,
  RemoveOrderItemResponse,
} from "@/features/order/types";
import {
  buildOrderItemDisplay,
  getOrderItemMoneyDisplay,
} from "@/features/order/display";
import { formatBRL, formatMoneyInput, parseMoneyInput } from "@/lib/timeAgo";
import {
  formatQuantityDisplay,
  getDefaultQuantity,
  getQuantityInputMode,
  getQuantityInputStep,
  getQuantityUnitLabel,
  getWholeDirectKgNonNegativeQuantityInputAttributes,
  getWholeDirectKgQuantityInputAttributes,
  isLegacyDirectKgItem,
  parseNonNegativeQuantityInput,
  parsePositiveQuantityInput,
  validateWholeDirectKgNonNegativeQuantityInput,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";
import { orderInputStyle, orderLabelStyle } from "./orderStyles";
import { LegacyKgMigrationPanel } from "./LegacyKgMigrationPanel";
import { OrderItemReplacementModal } from "./OrderItemReplacementModal";

type OrderItemRowProps = {
  item: EnrichedOrderItem;
  orderId: string;
  expectedVersion: number;
  orderStatus: OrderStatus;
  isSuper: boolean;
  canManageOrders?: boolean;
  canRemoveItem?: boolean;
  isStale: boolean;
  readOnly?: boolean;
  onRemove?: (response: RemoveOrderItemResponse) => void;
  onFeedback?: (feedback: {
    title: string;
    message?: string;
    tone: "success" | "error";
  }) => void;
};

type EditorAction = "edit" | "bonus";

function InlineEditPanel({
  children,
  name,
  supplier,
}: {
  children: ReactNode;
  name: string;
  supplier: string;
}) {
  return (
    <div className="order-item-inline-panel">
      <div className="order-item-inline-heading">
        <strong>{name}</strong>
        <span>{supplier}</span>
      </div>
      <div className="order-item-inline-fields">{children}</div>
    </div>
  );
}

export function OrderItemRow({
  item,
  orderId,
  expectedVersion,
  orderStatus,
  isSuper,
  canManageOrders = isSuper,
  canRemoveItem = isSuper || !canManageOrders,
  isStale,
  readOnly = false,
  onRemove,
  onFeedback,
}: OrderItemRowProps) {
  const updateItem = useUpdateOrderItem();
  const removeItem = useRemoveOrderItem();
  const setBonus = useSetOrderItemBonus();
  const editQuantityInputRef = useRef<HTMLInputElement>(null);
  const bonusInputRef = useRef<HTMLInputElement>(null);
  const editActionRef = useRef<HTMLButtonElement>(null);
  const bonusActionRef = useRef<HTMLButtonElement>(null);
  const removeActionRef = useRef<HTMLButtonElement>(null);
  const confirmRemoveActionRef = useRef<HTMLButtonElement>(null);
  const editId = useId();
  const editorActionRef = useRef<EditorAction | null>(null);
  const pendingFocusRestoreRef = useRef<EditorAction | null>(null);
  const pendingRemoveFocusRestoreRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [editingBonus, setEditingBonus] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [showLegacyKgMigration, setShowLegacyKgMigration] = useState(false);
  const [showReplacement, setShowReplacement] = useState(false);
  const [editQty, setEditQty] = useState(
    formatQuantityDisplay(item.quantity, item.unit_type),
  );
  const [bonusQty, setBonusQty] = useState(
    formatQuantityDisplay(item.bonus_quantity ?? 0, item.unit_type),
  );
  const [editPrice, setEditPrice] = useState<string>(
    item.final_price !== item.computed_price
      ? formatMoneyInput(item.final_price)
      : "",
  );
  const [error, setError] = useState<string | null>(null);

  const {
    isAvailable,
    isReturned,
    canEdit,
    hasSpecialPrice,
    bonus,
    returnBadge,
  } = buildOrderItemDisplay({
    item,
    orderStatus,
    isSuper,
    canManageOrders,
    isStale,
  });
  const money = getOrderItemMoneyDisplay(item);
  const isLegacyDirectKg = isLegacyDirectKgItem(item);
  const isReadOnly = readOnly || (isLegacyDirectKg && !isSuper);
  const canShowActions =
    !isReadOnly && (canEdit || (isSuper && isLegacyDirectKg));
  const canReplaceItem =
    isSuper &&
    !isReadOnly &&
    !isLegacyDirectKg &&
    item.return_status === "NONE";
  const isWholeDirectKg =
    item.unit_type === "kg" && item.direct_kg_quantity_mode === "whole";
  const itemMutationPending =
    updateItem.isPending || setBonus.isPending || removeItem.isPending;

  useEffect(() => {
    if (!isReadOnly) return;
    setEditing(false);
    setEditingBonus(false);
    setConfirmingRemoval(false);
  }, [isReadOnly]);

  useEffect(() => {
    if (editing) {
      editQuantityInputRef.current?.focus();
      return;
    }
    if (editingBonus) bonusInputRef.current?.focus();
  }, [editing, editingBonus]);

  useEffect(() => {
    if (editing || editingBonus) return;

    const focusOrigin = pendingFocusRestoreRef.current;
    if (!focusOrigin) return;
    pendingFocusRestoreRef.current = null;

    const focusTarget =
      focusOrigin === "bonus" ? bonusActionRef.current : editActionRef.current;
    const fallbackTarget =
      editorActionRef.current === "bonus"
        ? bonusActionRef.current
        : editActionRef.current;
    (focusTarget ?? fallbackTarget)?.focus();
  }, [editing, editingBonus]);

  useEffect(() => {
    if (confirmingRemoval) {
      if (!removeItem.isPending) {
        confirmRemoveActionRef.current?.focus();
      }
      return;
    }

    if (!pendingRemoveFocusRestoreRef.current) return;
    pendingRemoveFocusRestoreRef.current = false;
    removeActionRef.current?.focus();
  }, [confirmingRemoval, removeItem.isPending]);

  const directKgValidation = isWholeDirectKg
    ? validateWholeDirectKgQuantityInput(editQty)
    : null;
  const parsedEditedQty = directKgValidation
    ? directKgValidation.quantity
    : parsePositiveQuantityInput(editQty, item.unit_type);
  const qtyChanged =
    editing && (parsedEditedQty ?? item.quantity) !== item.quantity;
  const showSpecialWarning = qtyChanged && hasSpecialPrice && editPrice === "";

  async function handleSave() {
    if (isReadOnly || itemMutationPending) return;
    setError(null);
    if (directKgValidation?.reason) {
      const feedback = {
        title: "Quantidade inválida",
        message:
          "Para itens vendidos por kg, informe um número inteiro maior que zero.",
        tone: "error" as const,
      };
      onFeedback?.(feedback);
      if (!onFeedback) setError(feedback.title);
      return;
    }
    try {
      const payload: { quantity?: number; final_price?: number } = {};
      const parsedQty = isWholeDirectKg
        ? directKgValidation!.quantity!
        : (parsedEditedQty ?? item.quantity);
      if (parsedQty !== item.quantity) payload.quantity = parsedQty;
      if (canManageOrders && editPrice !== "") {
        const fp = parseMoneyInput(editPrice);
        if (fp != null) payload.final_price = fp;
      }
      if (Object.keys(payload).length > 0) {
        await updateItem.mutateAsync({ orderId, itemId: item.id, payload });
      }
      closeEditAndRestoreFocus();
    } catch (e) {
      const message = getOrderMutationErrorMessage(e);
      onFeedback?.({ title: message, tone: "error" });
      setError(message);
    }
  }

  async function handleSaveBonus() {
    if (isReadOnly || itemMutationPending) return;
    setError(null);
    try {
      const rawBonusQty = bonusInputRef.current?.value ?? bonusQty;
      const wholeDirectKgBonusValidation = isWholeDirectKg
        ? validateWholeDirectKgNonNegativeQuantityInput(rawBonusQty)
        : null;
      if (wholeDirectKgBonusValidation?.reason) {
        const feedback = {
          title: "Quantidade bonificada inválida",
          message:
            "Para itens vendidos por kg, informe um número inteiro igual ou maior que zero.",
          tone: "error" as const,
        };
        onFeedback?.(feedback);
        if (!onFeedback) setError(feedback.title);
        return;
      }
      const parsed = wholeDirectKgBonusValidation
        ? wholeDirectKgBonusValidation.quantity
        : parseNonNegativeQuantityInput(rawBonusQty, item.unit_type);
      if (parsed == null) {
        setError("Informe uma quantidade bonificada valida.");
        return;
      }
      if (parsed > item.quantity) {
        setError("Bonificação não pode passar da quantidade do item.");
        return;
      }
      await setBonus.mutateAsync({
        orderId,
        itemId: item.id,
        payload: { bonus_quantity: parsed },
      });
      closeBonusEditAndRestoreFocus();
    } catch (e) {
      const message = getOrderMutationErrorMessage(e);
      onFeedback?.({ title: message, tone: "error" });
      setError(message);
    }
  }

  async function handleRemove() {
    if (
      isReadOnly ||
      itemMutationPending ||
      !canRemoveItem ||
      (isSuper && !confirmingRemoval)
    ) {
      return;
    }
    setError(null);
    try {
      const response = await removeItem.mutateAsync({
        orderId,
        itemId: item.id,
      });
      setConfirmingRemoval(false);
      onRemove?.(response);
      document
        .querySelector<HTMLButtonElement>(
          `[data-testid="order-card-toggle-${CSS.escape(orderId)}"]`,
        )
        ?.focus();
    } catch (e) {
      const message = getOrderMutationErrorMessage(e);
      onFeedback?.({ title: message, tone: "error" });
      setError(message);
    }
  }

  function requestRemove() {
    if (isReadOnly || itemMutationPending || !canRemoveItem) return;
    if (!isSuper) {
      void handleRemove();
      return;
    }

    setError(null);
    pendingRemoveFocusRestoreRef.current = false;
    setConfirmingRemoval(true);
  }

  function cancelRemove() {
    if (itemMutationPending) return;
    pendingRemoveFocusRestoreRef.current = true;
    setConfirmingRemoval(false);
  }

  function startEdit() {
    if (isReadOnly || itemMutationPending) return;
    editorActionRef.current = "edit";
    pendingFocusRestoreRef.current = null;
    setEditing(true);
    setEditingBonus(false);
    setEditQty(formatQuantityDisplay(item.quantity, item.unit_type));
    setEditPrice(hasSpecialPrice ? formatMoneyInput(item.charged_price) : "");
  }

  function startLegacyKgMigration() {
    if (!isSuper || !isLegacyDirectKg) return;
    setShowLegacyKgMigration(true);
  }

  function startBonusEdit() {
    if (isReadOnly || itemMutationPending) return;
    editorActionRef.current = "bonus";
    pendingFocusRestoreRef.current = null;
    setEditing(false);
    setEditingBonus(true);
    setBonusQty(
      formatQuantityDisplay(item.bonus_quantity ?? 0, item.unit_type),
    );
  }

  function closeEditAndRestoreFocus() {
    pendingFocusRestoreRef.current = "edit";
    setEditing(false);
  }

  function closeBonusEditAndRestoreFocus() {
    pendingFocusRestoreRef.current = "bonus";
    setEditingBonus(false);
  }

  const unit = getQuantityUnitLabel(item.unit_type);
  const bonusQuantityLabel = `${formatQuantityDisplay(
    bonus.bonusQuantity,
    item.unit_type,
  )} ${unit}`;
  const billableQuantityLabel = `${formatQuantityDisplay(
    bonus.billableQuantity,
    item.unit_type,
  )} ${unit} cobradas`;
  const wholeDirectKgBonusValidation = isWholeDirectKg
    ? validateWholeDirectKgNonNegativeQuantityInput(bonusQty)
    : null;
  const parsedBonusQty = wholeDirectKgBonusValidation
    ? wholeDirectKgBonusValidation.quantity
    : parseNonNegativeQuantityInput(bonusQty, item.unit_type);
  const previewBillableQuantity =
    parsedBonusQty == null
      ? bonus.billableQuantity
      : Math.max(item.quantity - parsedBonusQty, 0);
  const previewBillableQuantityLabel = `${formatQuantityDisplay(
    previewBillableQuantity,
    item.unit_type,
  )} ${unit}`;
  return (
    <div
      className="order-item"
      role="group"
      aria-label={`Item ${item.product_name} de ${item.supplier_name}`}
      data-testid={`order-item-row-${item.id}`}
      data-ledger-row="true"
      data-confirming-removal={confirmingRemoval ? "true" : undefined}
      data-inline-mode={
        editing || editingBonus || confirmingRemoval || undefined
      }
      style={{
        opacity: !isAvailable || isReturned ? 0.45 : isStale ? 0.5 : 1,
      }}
    >
      <div
        className="order-item-main"
        data-testid={`order-item-grid-${item.id}`}
      >
        <div className="order-item-head" style={{ minWidth: 0 }}>
          <div
            className="order-item-product-head"
            data-testid={`order-item-product-head-${item.id}`}
          >
            <p
              className="order-item-product"
              title={item.product_name}
              style={{
                color:
                  !isAvailable || isReturned
                    ? "var(--login-muted)"
                    : "var(--login-text)",
                margin: 0,
                textDecoration:
                  !isAvailable || isReturned ? "line-through" : "none",
              }}
            >
              {item.product_name}
            </p>
            {bonus.hasBonus && (
              <span className="order-item-bonus order-item-bonus--mobile">
                Bonificação · {bonusQuantityLabel}
              </span>
            )}
            {!isAvailable && (
              <span
                style={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  color: "var(--login-muted)",
                  background: "var(--login-border)",
                  padding: "1px 6px",
                  borderRadius: "3px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  flexShrink: 0,
                }}
              >
                Indisponivel
              </span>
            )}
          </div>
          <div
            className="order-item-supplier-summary"
            data-testid={`order-item-supplier-summary-${item.id}`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.775rem",
              color: "var(--login-muted)",
              margin: "0.125rem 0 0",
            }}
          >
            <span
              className="order-item-supplier"
              title={item.supplier_name}
              style={{
                textDecoration:
                  !isAvailable || isReturned ? "line-through" : "none",
              }}
            >
              {item.supplier_name}
            </span>
            <span className="order-item-unit"> · {unit}</span>
            {bonus.hasBonus && (
              <span className="order-item-billable">
                {billableQuantityLabel}
              </span>
            )}
            {isLegacyDirectKg && (
              <span
                className="order-item-legacy-notice"
                style={{
                  color: "var(--login-muted)",
                  fontSize: "0.72rem",
                }}
              >
                {isSuper
                  ? "Registro histórico — migração necessária."
                  : "Registro histórico — somente visualização."}
              </span>
            )}
          </div>
          {(item.return_request_note || item.return_decision_note) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.18rem",
                marginTop: "0.35rem",
              }}
            >
              {item.return_request_note && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.72rem",
                    lineHeight: 1.45,
                    color: "#7c3aed",
                  }}
                >
                  Motivo: {item.return_request_note}
                </p>
              )}
              {item.return_decision_note && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.72rem",
                    lineHeight: 1.45,
                    color: isReturned ? "#6b7280" : "#0f766e",
                  }}
                >
                  Decisão: {item.return_decision_note}
                </p>
              )}
            </div>
          )}
        </div>

        <div
          className="order-item-meta"
          data-testid={`order-item-meta-${item.id}`}
        >
          {returnBadge && (
            <span
              className="order-item-return-status"
              style={
                {
                  "--return-status-color": returnBadge.color,
                } as CSSProperties
              }
            >
              <i aria-hidden="true" />
              {returnBadge.label}
            </span>
          )}
          {bonus.hasBonus && (
            <span className="order-item-bonus order-item-bonus--desktop">
              Bonificação · {bonusQuantityLabel}
            </span>
          )}
          <span className="order-item-qty mono">
            <span className="order-item-visually-hidden">Quantidade</span>
            <span>
              {formatQuantityDisplay(item.quantity, item.unit_type)} {unit}
            </span>
          </span>

          <div className="order-item-price item-price mono">
            <span className="order-item-visually-hidden">Total</span>
            <span className="order-item-money-current">
              {formatBRL(money.totalPrice)}
            </span>
            {money.comparisonTotalPrice !== null && (
              <del className="order-item-money-comparison">
                {formatBRL(money.comparisonTotalPrice)}
              </del>
            )}
          </div>
        </div>

        {(canShowActions || canReplaceItem) && (
          <div className="order-item-controls">
            <div
              className="order-item-direct-actions"
              data-testid={`order-item-direct-actions-${item.id}`}
            >
              <button
                ref={editActionRef}
                type="button"
                className="order-item-direct-action"
                onClick={isLegacyDirectKg ? startLegacyKgMigration : startEdit}
                aria-label="Editar"
                title="Editar"
                disabled={itemMutationPending}
              >
                <Pencil size={15} />
              </button>
              {canReplaceItem && (
                <button
                  type="button"
                  className="order-item-direct-action"
                  onClick={() => setShowReplacement(true)}
                  aria-label="Substituir item"
                  title="Substituir item"
                  disabled={itemMutationPending}
                >
                  <RefreshCw size={15} />
                </button>
              )}
              {canManageOrders && (
                <button
                  ref={bonusActionRef}
                  type="button"
                  className="order-item-direct-action"
                  onClick={startBonusEdit}
                  aria-label="Bonificação"
                  title="Bonificação"
                  disabled={itemMutationPending}
                >
                  <Gift size={15} />
                </button>
              )}
              {canRemoveItem ? (
                <button
                  ref={removeActionRef}
                  type="button"
                  className="order-item-direct-action danger"
                  onClick={requestRemove}
                  disabled={itemMutationPending}
                  aria-label="Excluir"
                  title="Excluir"
                >
                  <Trash2 size={15} />
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {editing && !isReadOnly && (
        <InlineEditPanel name={item.product_name} supplier={item.supplier_name}>
          <div className="order-item-edit-inputs">
            <div
              className="order-item-inline-field"
              style={{
                flex: 1,
              }}
            >
              <label htmlFor={`${editId}-quantity`} style={orderLabelStyle}>
                Qtd
              </label>
              <input
                id={`${editId}-quantity`}
                ref={editQuantityInputRef}
                type="number"
                {...(isWholeDirectKg
                  ? getWholeDirectKgQuantityInputAttributes()
                  : {
                      min: getDefaultQuantity(item.unit_type),
                      step: getQuantityInputStep(item.unit_type),
                      inputMode: getQuantityInputMode(item.unit_type),
                    })}
                value={editQty}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditQty(v);
                  if (
                    (isWholeDirectKg
                      ? validateWholeDirectKgQuantityInput(v).quantity
                      : parsePositiveQuantityInput(v, item.unit_type)) !==
                    item.quantity
                  ) {
                    setEditPrice("");
                  }
                }}
                onBlur={() => {
                  if (isWholeDirectKg) return;
                  const nextQty = parsePositiveQuantityInput(
                    editQty,
                    item.unit_type,
                  );
                  setEditQty(
                    formatQuantityDisplay(
                      nextQty ?? item.quantity,
                      item.unit_type,
                    ),
                  );
                }}
                onFocus={(e) => e.target.select()}
                style={{
                  ...orderInputStyle,
                  padding: "0.5rem 0.625rem",
                  fontSize: "0.9375rem",
                }}
              />
            </div>
            {canManageOrders && (
              <div
                className="order-item-inline-field"
                style={{
                  flex: 1,
                }}
              >
                <label htmlFor={`${editId}-price`} style={orderLabelStyle}>
                  Preco especial (R$)
                </label>
                <input
                  id={`${editId}-price`}
                  type="text"
                  inputMode="numeric"
                  placeholder={formatBRL(item.computed_price)}
                  value={editPrice}
                  onChange={(e) =>
                    setEditPrice(formatMoneyInput(e.target.value))
                  }
                  style={{
                    ...orderInputStyle,
                    padding: "0.5rem 0.625rem",
                    fontSize: "0.9375rem",
                  }}
                />
              </div>
            )}
          </div>
          {showSpecialWarning && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                padding: "0.375rem 0.5rem",
                background: "#d9770612",
                borderRadius: "0.25rem",
              }}
            >
              <AlertTriangle
                size={12}
                color="#d97706"
                style={{ flexShrink: 0 }}
              />
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.75rem",
                  color: "#d97706",
                }}
              >
                Preco especial sera removido
              </span>
            </div>
          )}
          <div className="order-item-inline-actions">
            <button
              type="button"
              onClick={closeEditAndRestoreFocus}
              disabled={itemMutationPending}
            >
              <X size={15} /> Cancelar
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void handleSave()}
              disabled={itemMutationPending}
            >
              <Check size={15} /> Salvar
            </button>
          </div>
        </InlineEditPanel>
      )}

      {editingBonus && !isReadOnly && (
        <InlineEditPanel name={item.product_name} supplier={item.supplier_name}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(96px, 10rem)",
              gap: "0.5rem",
              alignItems: "end",
            }}
          >
            <div
              style={{
                minWidth: 0,
                minHeight: "44px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "0.05rem",
              }}
            >
              <span
                className="order-item-bonus-preview-label"
                title="Cobrados pós bonificação"
              >
                Cobrados
                <span className="bonus-full-label"> pós bonificação</span>
              </span>
              <strong className="order-item-bonus-preview-quantity">
                {previewBillableQuantityLabel}
              </strong>
            </div>
            <div
              className="order-item-inline-field"
              style={{
                minWidth: 0,
              }}
            >
              <label
                className="order-item-bonus-field-label"
                htmlFor={`${editId}-bonus`}
              >
                Bonificação
              </label>
              <input
                id={`${editId}-bonus`}
                ref={bonusInputRef}
                aria-label="Bonificação — quantidade bonificada"
                type="number"
                {...(isWholeDirectKg
                  ? getWholeDirectKgNonNegativeQuantityInputAttributes()
                  : {
                      min: 0,
                      step: getQuantityInputStep(item.unit_type),
                      inputMode: getQuantityInputMode(item.unit_type),
                    })}
                max={item.quantity}
                value={bonusQty}
                onChange={(e) => setBonusQty(e.target.value)}
                onBlur={() => {
                  if (isWholeDirectKg) return;
                  const nextQty = parseNonNegativeQuantityInput(
                    bonusQty,
                    item.unit_type,
                  );
                  if (nextQty != null) {
                    setBonusQty(formatQuantityDisplay(nextQty, item.unit_type));
                  }
                }}
                onFocus={(e) => e.target.select()}
                style={{
                  ...orderInputStyle,
                  padding: "0.5rem 0.625rem",
                  fontSize: "0.9375rem",
                }}
              />
            </div>
          </div>
          <div className="order-item-inline-actions">
            <button
              type="button"
              onClick={closeBonusEditAndRestoreFocus}
              disabled={itemMutationPending}
            >
              <X size={15} /> Cancelar
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void handleSaveBonus()}
              disabled={itemMutationPending}
            >
              <Check size={15} /> Salvar
            </button>
          </div>
        </InlineEditPanel>
      )}

      {confirmingRemoval && isSuper && canRemoveItem && !isReadOnly && (
        <InlineEditPanel name={item.product_name} supplier={item.supplier_name}>
          <div
            className="order-item-remove-confirmation"
            data-testid={`order-item-remove-confirmation-${item.id}`}
            role="group"
            aria-label={`Confirmar exclusão de ${item.product_name}`}
          >
            <span>Excluir item?</span>
            <button
              ref={confirmRemoveActionRef}
              type="button"
              className="order-item-remove-confirmation-button danger"
              onClick={() => void handleRemove()}
              disabled={itemMutationPending}
              aria-label="Confirmar exclusão"
            >
              <Trash2 size={14} /> Confirmar
            </button>
            <button
              type="button"
              className="order-item-remove-confirmation-button"
              onClick={cancelRemove}
              disabled={itemMutationPending}
              aria-label="Cancelar exclusão"
            >
              <X size={14} /> Cancelar
            </button>
          </div>
        </InlineEditPanel>
      )}

      {error && (
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8rem",
            color: "var(--color-error, #ef4444)",
            margin: 0,
            marginTop: "0.25rem",
          }}
        >
          {error}
        </p>
      )}

      {showLegacyKgMigration && isSuper && isLegacyDirectKg && (
        <LegacyKgMigrationPanel
          orderId={orderId}
          itemId={item.id}
          onClose={() => setShowLegacyKgMigration(false)}
          onSuccess={() => {
            onFeedback?.({
              title: "Item histórico migrado.",
              tone: "success",
            });
          }}
        />
      )}
      {showReplacement && canReplaceItem && (
        <OrderItemReplacementModal
          item={item}
          expectedVersion={expectedVersion}
          onClose={() => setShowReplacement(false)}
          onSuccess={(result) =>
            onFeedback?.({
              title: "Item substituído.",
              message: result,
              tone: "success",
            })
          }
        />
      )}
    </div>
  );
}
